'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { InfoAlert } from '@/General/InfoAlert'
import { useCookie } from '@/Cookies/useCookies'
import { CHECKOUT_MARKET_MOVE_COOKIE } from '@/lib/auth/marketCookies'
import { getBrandCodeForHost } from '@/lib/seo/marketUrls'
import { tracking } from '@/tracking'
import { useQueryClient } from '@tanstack/react-query'

type CheckoutMarketMoveNoticeProps = {
  /** When set, copy matches the cart API so we do not promise GBP while totals are still USD. */
  cartCurrencyCode?: string | null
}

const MARKET_MOVE_REFETCH_ATTEMPTS = 8
const MARKET_MOVE_REFETCH_INTERVAL_MS = 750

function hasCheckoutMarketMoveCookie(): boolean {
  if (typeof document === 'undefined') return false

  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${CHECKOUT_MARKET_MOVE_COOKIE}=`))
    ?.split('=')[1]
  return cookie === '1'
}

export function CheckoutMarketMoveNotice({
  cartCurrencyCode = null,
}: CheckoutMarketMoveNoticeProps) {
  const { setCookie } = useCookie()
  const queryClient = useQueryClient()
  const trackedOpenRef = useRef(false)
  const cartCurrencyCodeRef = useRef(cartCurrencyCode)
  const [hadMarketMove] = useState(hasCheckoutMarketMoveCookie)
  const [open, setOpen] = useState(hadMarketMove)
  const brand =
    typeof window !== 'undefined'
      ? getBrandCodeForHost(window.location.host)
      : null
  const isUk = brand === 'VANISHING_INC_UK'

  useEffect(() => {
    cartCurrencyCodeRef.current = cartCurrencyCode
  }, [cartCurrencyCode])

  useEffect(() => {
    if (hadMarketMove) {
      setCookie(CHECKOUT_MARKET_MOVE_COOKIE, '', 0)
    }
  }, [hadMarketMove, setCookie])

  useEffect(() => {
    if (!hadMarketMove) return

    let cancelled = false
    let timeoutId: number | null = null
    let attempt = 0
    const targetCurrency = isUk ? 'GBP' : 'USD'

    const refetchCheckoutMarketData = async () => {
      await queryClient.invalidateQueries({
        queryKey: ['ActiveShoppingCart'],
        exact: false,
      })
      await queryClient.refetchQueries({
        queryKey: ['ActiveShoppingCart'],
        exact: false,
      })
      await queryClient.invalidateQueries({
        queryKey: ['AvailablePaymentMethodsForShoppingCart'],
        exact: false,
      })
      await queryClient.refetchQueries({
        queryKey: ['AvailablePaymentMethodsForShoppingCart'],
        exact: false,
      })
    }

    const tick = async () => {
      attempt += 1
      try {
        await refetchCheckoutMarketData()
      } catch {
        // A later attempt may succeed once the handoff/auth cookies settle.
      }
      if (cancelled) return

      const currentCurrency = cartCurrencyCodeRef.current?.trim().toUpperCase()
      if (
        currentCurrency === targetCurrency ||
        attempt >= MARKET_MOVE_REFETCH_ATTEMPTS
      ) {
        return
      }

      timeoutId = window.setTimeout(() => {
        void tick()
      }, MARKET_MOVE_REFETCH_INTERVAL_MS)
    }

    void tick()

    return () => {
      cancelled = true
      if (timeoutId != null) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [hadMarketMove, isUk, queryClient])

  useEffect(() => {
    if (!open) return
    if (trackedOpenRef.current) return

    trackedOpenRef.current = true
    tracking.track('Checkout market moved', {
      destination: isUk ? 'uk' : 'us',
    })
  }, [isUk, open])

  const dismiss = useCallback(() => {
    setOpen(false)
  }, [])

  if (!open) return null

  const cartCode = cartCurrencyCode?.trim().toUpperCase() || null
  const cartShowsGbp = cartCode === 'GBP'
  const cartShowsUsd = cartCode === 'USD' || cartCode == null

  return (
    <div className="mt-3 pb-1">
      <InfoAlert variant="info" className="relative mb-0">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss checkout market notice"
          className="absolute right-3 top-3 rounded-full p-1 text-blue-700 transition hover:bg-blue-100 hover:text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="mb-2 pr-8 text-xl font-bold text-gray-900">
          {isUk
            ? 'You are now on our UK checkout'
            : 'You are now on our US checkout'}
        </div>
        {isUk ? (
          <p className="text-base leading-relaxed">
            {cartShowsGbp ? (
              <>
                We moved you to our UK site so prices, tax, and shipping match
                your order. Amounts are shown in <strong>GBP (£)</strong> and UK
                payment options apply.
              </>
            ) : cartShowsUsd ? (
              <>
                We moved you to our UK checkout. Your basket is still priced in{' '}
                <strong>US dollars ($)</strong> while we switch it to GBP — this
                usually takes a few seconds. UK payment options apply once
                pricing updates.
              </>
            ) : (
              <>
                We moved you to our UK checkout so prices, tax, and shipping can
                match your order. Totals will show in <strong>GBP (£)</strong>{' '}
                when pricing has finished updating.
              </>
            )}
          </p>
        ) : (
          <p className="text-base leading-relaxed">
            {cartCode === 'USD' || cartCode == null ? (
              <>
                We moved you to our US site so prices, tax, and shipping match
                your order. Amounts are shown in <strong>USD ($)</strong> and US
                payment options apply.
              </>
            ) : cartCode === 'GBP' ? (
              <>
                We moved you to our US checkout. Your basket is still priced in{' '}
                <strong>British pounds (£)</strong> while we switch it to US
                dollars — this usually takes a few seconds.
              </>
            ) : (
              <>
                We moved you to our US checkout so prices, tax, and shipping can
                match your order. Totals will show in <strong>USD ($)</strong>{' '}
                when pricing has finished updating.
              </>
            )}
          </p>
        )}
      </InfoAlert>
    </div>
  )
}
